import { Injectable } from "@angular/core";
import { Effect, ofType, Actions } from "@ngrx/effects";
import { switchMap, catchError, withLatestFrom } from "rxjs/operators";
import { of } from "rxjs";
import { RequestAPIError } from "@edp-store/actions/dashboard.actions";
import {
    EReadingActions,
    SetUploadReadingGas,
    UploadReadingGas,
    UploadReadingElectricity,
    SetUploadReadingElectricity, GetReadings, SetReadings,
} from "@edp-store/actions/reading.actions";
import { ConsumptionsService } from "@edp-services/consumptions.service";
import { GetRatingStatus } from "@edp-store/actions/rating.actions";
import { ReadingsService } from "@edp-pages/readings/components/readings.service";
import { Store } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { Contract } from "@edp-components/edp-contract";
import moment from "moment";
import { MeddaliaTrackingService } from "@edp-services/meddalia-tracking.service";

@Injectable()
export class ReadingEffects {
    @Effect()
    getReadings = this.actions$.pipe(
        ofType(EReadingActions.GetReadings),
        withLatestFrom(this.store.select(getContract)),
        switchMap(([action, contract]: [GetReadings, Contract]) => {
            return this.readingService.getReadingsByType(contract.ContractId, action.supplyType, action.meterId);
        }),
        switchMap((readings) => {
            readings.forEach(readings => {
                readings.forEach(reading => {
                    reading.startBillingPeriod = reading.startBillingPeriod;
                    reading.showEstimationsDays = this.setShowReadingEstimation(reading.endBillingPeriod, reading.readingDate);
                    reading.endBillingPeriod = reading.endBillingPeriod;
                    reading.readingDate = reading.readingDate;
                });
            });
            return of(new SetReadings(readings));
        })
    );

    @Effect()
    loadUploadReadingElectricity$ = this.actions$.pipe(
        ofType(EReadingActions.UploadReadingElectricity),
        switchMap((action: UploadReadingElectricity) => {
            return this.consumptionsService
                .uploadReading(action.reading)
                .pipe(catchError((error: Error) => of(new RequestAPIError(error))));
        }),
        switchMap(reading => {
            const success = !(reading instanceof RequestAPIError);
            if (success) {
                this.meddaliaTrackingService.setMedalliaTracking("Envio de leituras");
            }
            return of(new SetUploadReadingElectricity(success), new GetRatingStatus());
        })
    );

    @Effect()
    loadUploadReadingGas$ = this.actions$.pipe(
        ofType(EReadingActions.UploadReadingGas),
        switchMap((action: UploadReadingGas) => {
            return this.consumptionsService
                .uploadReading(action.reading)
                .pipe(catchError((error: Error) => of(new RequestAPIError(error))));
        }),
        switchMap(reading => {
            const success = !(reading instanceof RequestAPIError);
            if (success) {
                this.meddaliaTrackingService.setMedalliaTracking("Envio de leituras");
            }
            return of(new SetUploadReadingGas(success), new GetRatingStatus());
        })
    );

    constructor(
        private readonly actions$: Actions,
        private readonly consumptionsService: ConsumptionsService,
        private readonly readingService: ReadingsService,
        private readonly store: Store<IAppState>,
        private readonly meddaliaTrackingService: MeddaliaTrackingService
    ) { }

    private setDates(date: string) {
        return moment(date)
            .locale("pt-pt")
            .format("DD MMM YYYY")
            .toString();
    }

    private setShowReadingEstimation(endBillingPeriod: string, date: string) {
        const minDaysToShowEstimations = 4;
        const startDayOfEachMonth = moment(endBillingPeriod, "YYYY-MM-DD").date();
        const readingDay = moment(date, "YYYY-MM-DD").date();
        return Math.abs(startDayOfEachMonth - readingDay) >= minDaysToShowEstimations;
    }
}
